import { ResizableNodeView, ResizableRatioType } from 'prosemirror-resizable-view';
import { setStyle } from '@remirror/core';
import type { EditorView, NodeView, ProsemirrorNode } from '@remirror/pm';

// Helper function to detect if a file is a video based on its URL
export function isVideoFile(src: string): boolean {
  if (!src) return false;

  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.m4v', '.mkv', '.wmv', '.flv'];
  const lowercaseSrc = src.toLowerCase();

  return videoExtensions.some(ext => lowercaseSrc.includes(ext)) || lowercaseSrc.includes('video/');
}

/**
 * ResizableImageView is a NodeView for images and videos. You can resize the media by
 * dragging the handle over the element.
 */
export class ResizableImageView extends ResizableNodeView implements NodeView {
  constructor(node: ProsemirrorNode, view: EditorView, getPos: () => number) {
    super({ node, view, getPos, aspectRatio: ResizableRatioType.Fixed });
  }

  createElement({ node }: { node: ProsemirrorNode }): HTMLImageElement | HTMLVideoElement {
    const isVideo = isVideoFile(node.attrs.src as string);

    if (isVideo) {
      const video = document.createElement('video');
      video.setAttribute('src', node.attrs.src as string);
      video.setAttribute('data-cid', node.attrs.cid as string);
      video.setAttribute('controls', 'true');
      video.setAttribute('preload', 'metadata');

      setStyle(video, {
        width: '100%',
        minWidth: '100px',
        maxWidth: '100%',
        height: 'auto',
        objectFit: 'contain',
        borderRadius: '5px',
      });

      return video;
    } else {
      const inner = document.createElement('img');
      inner.setAttribute('src', node.attrs.src as string);
      inner.setAttribute('data-cid', node.attrs.cid as string);

      setStyle(inner, {
        width: '100%',
        minWidth: '50px',
        objectFit: 'contain', // maintain image's aspect ratio
      });

      return inner;
    }
  }
}